/**
 * ContactBlock — Phase 1: Surface Intelligence
 * ─────────────────────────────────────────────
 * A single, reusable contact row. Enforces phone-first, email-second
 * ordering by construction so it can never drift across surfaces
 * (homeowner, contractor, vendor, etc.).
 *
 * Non-destructive: pure presentation. Outreach logging happens via
 * the optional onPhoneClick / onEmailClick callbacks.
 */
import { Mail, Phone } from "lucide-react";

export interface ContactBlockProps {
  name: string;
  /** Secondary line, e.g. "Homeowner", "Assigned crew", role, status. */
  subtitle?: string;
  phone?: string | null;
  email?: string | null;
  /** Subject line for the mailto: link. */
  emailSubject?: string;
  /** Fired when operator taps Call — useful for logging outreach. */
  onPhoneClick?: () => void;
  /** Fired when operator taps Email — useful for logging outreach. */
  onEmailClick?: () => void;
  /** Compact mode renders a single line without the subtitle row. */
  compact?: boolean;
  /** Optional badge / element rendered next to the name. */
  badge?: React.ReactNode;
}

export function ContactBlock({
  name,
  subtitle,
  phone,
  email,
  emailSubject,
  onPhoneClick,
  onEmailClick,
  compact = false,
  badge,
}: ContactBlockProps) {
  const telHref = phone ? `tel:${phone.replace(/[^0-9+]/g, "")}` : undefined;
  const mailHref = email
    ? `mailto:${email}${emailSubject ? `?subject=${encodeURIComponent(emailSubject)}` : ""}`
    : undefined;

  return (
    <div className="flex items-center gap-2 w-full">
      {/* Name + optional subtitle on the left, takes available space */}
      <div className="flex items-center gap-1.5 min-w-0 flex-1">
        <span className="text-xs font-semibold truncate">{name}</span>
        {badge}
        {subtitle && !compact && (
          <span className="text-[10px] text-muted-foreground truncate">· {subtitle}</span>
        )}
      </div>
      {/* Contacts pinned to the right — phone first, email second */}
      <div className="flex items-center gap-1.5 shrink-0 ml-auto">
        {telHref && (
          <a
            href={telHref}
            onClick={onPhoneClick}
            className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border bg-muted/40 hover:bg-primary hover:text-primary-foreground transition-colors"
            title={phone ?? undefined}
          >
            <Phone className="h-3 w-3" /> {phone}
          </a>
        )}
        {mailHref && (
          <a
            href={mailHref}
            onClick={onEmailClick}
            className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border bg-muted/40 hover:bg-primary hover:text-primary-foreground transition-colors"
            title={email ?? undefined}
          >
            <Mail className="h-3 w-3" /> Email
          </a>
        )}
      </div>
    </div>
  );
}
